import { safeJsonObject } from '../lib/safeJson'
import Select from '../ui/Select'
import { useState } from 'react'

import type { ColumnInfo } from '../api/client'
import { AlignIcon } from './AlignIcons'
import { askConfirm } from './Dialog'
import { IMPLEMENTED_COLUMN_TYPES, columnTypeLabel } from './columnTypes'

export interface ColumnCenterProps {
  columns: ColumnInfo[]
  /** 看板级设置（用来显示"跟随全局"实际是什么） */
  uiSettings: Record<string, string>
  busy: boolean
  onClose: () => void
  onDelete: (ids: number[]) => void
  onChangeType: (ids: number[], type: string) => void
  /** 传 '' 表示恢复"跟随全局" */
  onAlign: (ids: number[], align: string) => void
  onWidth: (ids: number[], width: number) => void
}

/** 和 BoardPage 的 ALIGN_LOCKED 保持一致：这些列不吃对齐设置 */
const ALIGN_LOCKED: Record<string, boolean> = {
  formula: true,
  boolean: true,
  tag: true,
  timerange: true,
  rating: true,
  color: true,
  dropdown: true,
  'columns-battery': true,
}

const ALIGN_CHOICES: { value: '' | 'left' | 'center' | 'right'; label: string; kind: 'auto' | 'left' | 'center' | 'right' }[] = [
  { value: '', label: '跟随全局', kind: 'auto' },
  { value: 'left', label: '左', kind: 'left' },
  { value: 'center', label: '中', kind: 'center' },
  { value: 'right', label: '右', kind: 'right' },
]

/** 从列 settings 里读对齐（空 = 跟随全局） */
function alignOf(col: ColumnInfo): string {
  const obj = safeJsonObject<Record<string, unknown>>(col.settings, {}, 'columnAlign')
  const a = obj.align
  return typeof a === 'string' ? a : ''
}

/**
 * 列中心（M11-1）：右侧抽屉，一次选中多列做批量操作。
 *
 * 形态与「条目详情」一致（复用 .detail-mask / .detail-panel 那套样式），
 * 只是窄一些、内容是列清单 + 底部批量操作区。
 *
 * 批量操作只有四项（用户指定）：删除、改类型、对齐、宽度。
 */
export default function ColumnCenter({
  columns,
  uiSettings,
  busy,
  onClose,
  onDelete,
  onChangeType,
  onAlign,
  onWidth,
}: ColumnCenterProps) {
  const [picked, setPicked] = useState<Set<number>>(new Set())
  const [nextType, setNextType] = useState<string>('text')
  const [nextWidth, setNextWidth] = useState<number>(160)

  const globalAlign = uiSettings.columnAlign ?? 'left'
  const ids = [...picked]
  const allPicked = columns.length > 0 && picked.size === columns.length
  /** 选中的列里，有多少列允许改对齐 */
  const alignableCount = columns.filter((c) => picked.has(c.id) && ALIGN_LOCKED[c.type] !== true).length

  const toggle = (id: number) => {
    const next = new Set(picked)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setPicked(next)
  }

  const toggleAll = () => {
    setPicked(allPicked ? new Set() : new Set(columns.map((c) => c.id)))
  }

  const doDelete = () => {
    void askConfirm(
      '批量删除列？',
      `将删除 ${ids.length} 列及其全部数据，且不可恢复。`,
      '删除',
    ).then((ok) => {
      if (ok) onDelete(ids)
    })
  }

  return (
    <>
      <div className="detail-mask" onClick={onClose} />
      <aside
        className="detail-panel detail-panel--cc"
        role="dialog"
        aria-modal="true"
        aria-label="列中心"
      >
        <header className="detail-panel__head">
          <span className="detail-panel__title">列中心</span>
          <div className="detail-panel__nav">
            <button
              type="button"
              className="detail-panel__btn"
              title="关闭"
              aria-label="关闭"
              onClick={onClose}
            >
              ✕
            </button>
          </div>
        </header>

        <div className="cc-toolbar">
          <label className="cc-check">
            <input type="checkbox" checked={allPicked} onChange={toggleAll} disabled={busy} />
            <span>全选</span>
          </label>
          <span className="dim">
            已选 {picked.size} / {columns.length}
          </span>
          <span className="dim">默认对齐：{globalAlign}</span>
        </div>

        <ul className="cc-list">
          {columns.map((c) => {
            const on = picked.has(c.id)
            const own = alignOf(c)
            return (
              <li key={c.id} className={on ? 'cc-row is-on' : 'cc-row'}>
                <input type="checkbox" checked={on} onChange={() => toggle(c.id)} disabled={busy} />
                <span className="cc-row__title" title={c.title}>
                  {c.title}
                </span>
                <span className="cc-row__type">{columnTypeLabel(c.type)}</span>
                <span className="cc-row__meta">
                  {c.width}px · {own === '' ? '跟随全局' : own}
                </span>
              </li>
            )
          })}
        </ul>

        <footer className="cc-foot">
          <div className="cc-group">
            <span className="cc-group__label">改变类型</span>
            <Select
              className="cc-select"
              value={nextType}
              onChange={(v) => setNextType(v)}
              disabled={busy}
            >
              {IMPLEMENTED_COLUMN_TYPES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </Select>
            <button
              type="button"
              className="cc-apply"
              disabled={busy || ids.length === 0 || nextType === ''}
              onClick={() => onChangeType(ids, nextType)}
            >
              应用
            </button>
          </div>

          <div className="cc-group">
            <span className="cc-group__label">对齐</span>
            {ALIGN_CHOICES.map((a) => (
              <button
                key={a.kind}
                type="button"
                className="align-btn"
                title={a.label}
                aria-label={a.label}
                disabled={busy || alignableCount === 0}
                onClick={() => onAlign(ids, a.value)}
              >
                <AlignIcon kind={a.kind} />
              </button>
            ))}
            <span className="dim">可改 {alignableCount} 列</span>
          </div>

          <div className="cc-group">
            <span className="cc-group__label">宽度</span>
            <input
              className="cc-input"
              type="number"
              min={40}
              max={800}
              step={10}
              value={nextWidth}
              onChange={(e) => setNextWidth(Number(e.target.value))}
              disabled={busy}
            />
            <span className="dim">px</span>
            <button
              type="button"
              className="cc-apply"
              disabled={busy || ids.length === 0 || !Number.isFinite(nextWidth)}
              onClick={() => onWidth(ids, Math.max(40, Math.min(800, Math.round(nextWidth))))}
            >
              应用
            </button>
          </div>

          <button
            type="button"
            className="cc-danger"
            disabled={busy || ids.length === 0}
            onClick={doDelete}
          >
            批量删除（{ids.length}）
          </button>
        </footer>
      </aside>
    </>
  )
}
